import { supabase } from "@/integrations/supabase/client";

export const loadUserProgress = async (userId: string) => {
  const [progressRes, examsRes, certsRes] = await Promise.all([
    supabase.from("user_progress").select("lesson_id").eq("user_id", userId),
    supabase
      .from("exam_results")
      .select("track_id, score, total, passed, completed_at, wrong_question_ids")
      .eq("user_id", userId),
    // SECURITY: cert_code is server-masked when paid=false. Never trust the client
    // about admin-bypass; the DB trigger auto-marks admin certs paid=true.
    supabase.rpc("get_my_certificates"),
  ]);

  const completedLessons = (progressRes.data ?? []).map((r) => r.lesson_id);
  // O(1) lookup instead of array.includes() — used in tight loops over 980+ ids.
  const completedSet = new Set(completedLessons);

  const examScores: Record<
    string,
    { score: number; total: number; passed: boolean; date: string; wrongQuestionIds: string[] }
  > = {};
  for (const e of examsRes.data ?? []) {
    examScores[e.track_id] = {
      score: e.score,
      total: e.total,
      passed: !!e.passed,
      date: e.completed_at,
      wrongQuestionIds: (e.wrong_question_ids ?? []) as string[],
    };
  }

  const certificates: Record<string, { date: string; score: number; certCode: string; paid: boolean }> = {};
  for (const c of (certsRes.data ?? []) as Array<{ track_id: string; score: number; issued_at: string; paid: boolean; cert_code: string | null }>) {
    certificates[c.track_id] = {
      date: c.issued_at,
      score: c.score,
      certCode: c.cert_code ?? "", // empty when unpaid — DB enforces the mask
      paid: !!c.paid,
    };
  }

  return { completedLessons, completedSet, examScores, certificates };
};

export type UserProgress = Awaited<ReturnType<typeof loadUserProgress>>;

export const toggleLessonProgress = async (userId: string, lessonId: string, isComplete: boolean) => {
  if (isComplete) {
    await supabase.from("user_progress").delete().eq("user_id", userId).eq("lesson_id", lessonId);
  } else {
    await supabase.from("user_progress").insert({ user_id: userId, lesson_id: lessonId });
  }
};

/**
 * Submit an exam attempt for server-side grading.
 *
 * The client only knows which choice the user picked for each question — it
 * never sends a "score" or "passed" flag. The `submit-exam` edge function
 * scores the answers against a server-bundled key and writes both the
 * exam_results row and (on pass) the certificate. Returns whether the user
 * passed so the UI can show the right result screen.
 */
export const saveExamResult = async (
  trackId: string,
  answers: Record<string, number>,
): Promise<{ passed: boolean; score: number; total: number }> => {
  const { data, error } = await supabase.functions.invoke("submit-exam", {
    body: { trackId, answers },
  });
  if (error || !data?.ok) {
    throw new Error(error?.message ?? data?.error ?? "exam_submit_failed");
  }
  return { passed: !!data.passed, score: data.score ?? 0, total: data.total ?? 0 };
};

export const getProgressStats = (
  completedLessons: string[] | Set<string>,
  lessonIds: string[],
) => {
  const set =
    completedLessons instanceof Set ? completedLessons : new Set(completedLessons);
  let completed = 0;
  for (const id of lessonIds) if (set.has(id)) completed++;
  return {
    completed,
    total: lessonIds.length,
    percentage: lessonIds.length > 0 ? Math.round((completed / lessonIds.length) * 100) : 0,
  };
};

/**
 * Check if a track is unlocked. Track 0 is always unlocked.
 * A track unlocks if the previous track's exam was passed (80%+).
 */
export const isTrackUnlocked = (
  trackIndex: number,
  _completedLessons: string[],
  _allTracks: { modules: { lessons: { id: string }[] }[] }[],
  examScores?: Record<string, { score: number; total: number }>,
  allTrackIds?: string[],
) => {
  if (trackIndex <= 0) return true;
  if (!examScores || !allTrackIds) return false;
  const prevTrackId = allTrackIds[trackIndex - 1];
  if (!prevTrackId) return true;
  const prev = examScores[prevTrackId];
  return !!prev && prev.total > 0 && prev.score / prev.total >= 0.8;
};

/**
 * Get the name of the prerequisite track (the one before this one).
 */
export const getPrerequisiteTrackName = (trackIndex: number, allTracks: { name: string }[]) => {
  if (trackIndex <= 0) return null;
  return allTracks[trackIndex - 1]?.name ?? null;
};
